<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { page } from '$app/state';
	import { Button } from '$global/components/Button';
	import { InfiniteScroll } from '$global/components/InfiniteScroll';
	import { getActiveArtistFromStores } from '$global/features/filter-drawer/utils/getActiveArtist/getActiveArtist';
	import { track } from '$global/utils/track';

	interface Props {
		dataCy: string;
		hasMore: boolean;
		onLoadMore: () => void;
		// When true, a "Load more" button is shown first and loading only
		// switches to scroll-triggered (infinite scroll) once it's been clicked.
		initialLoadMoreButton?: boolean;
		threshold?: number;
		// Analytics event-name prefix (e.g. 'SHOP'). When omitted, load-more
		// interactions are not tracked.
		trackPrefix?: string;
		// Number of items currently rendered. Used only to tell when a requested
		// page has landed, so repeated scroll triggers for the same page are
		// counted once. Without it, tracking falls back to counting every
		// trigger, which can over-report.
		loadedCount?: number;
		classes?: { button?: string };
	}

	let {
		dataCy,
		hasMore,
		onLoadMore,
		initialLoadMoreButton = false,
		threshold = 800,
		trackPrefix = undefined,
		loadedCount = undefined,
		classes = {},
	}: Props = $props();

	let hasClickedLoadMore = $state(false);

	// Page 1 is the initial load, so the first load-more fetches page 2.
	let pageIndex = 1;

	// Filter/sort changes navigate with new search params and reset the grid
	// back to its first page of results, so the counter must restart too.
	$effect(() => {
		void page.url.search;
		pageIndex = 1;
	});

	// InfiniteScroll releases its internal guard on scroll position, not on the
	// in-flight fetch: scrolling out of and back into the threshold re-fires
	// `onloadmore` for the page already being fetched. The underlying query
	// dedupes that, so the grid is unaffected, but a naive counter here would
	// report two LOAD_MOREs (with different page numbers) for one page. Only
	// count a request once the previous one has actually landed.
	let isAwaitingPage = $state(false);

	// Clears once the newly requested items land. Grids that don't report a
	// count can't be de-duped, so the flag is never set for them.
	$effect(() => {
		void loadedCount;
		isAwaitingPage = false;
	});

	const trackLoadMore = (source: 'button' | 'scroll') => {
		if (!trackPrefix || isAwaitingPage) return;

		isAwaitingPage = loadedCount !== undefined;
		pageIndex += 1;

		// Paging happens within whatever filters are active, so the artist
		// narrowing the grid rides along as a cross-cutting dimension.
		const artist = getActiveArtistFromStores();

		track(`${trackPrefix}/LOAD_MORE`, {
			load_more: source,
			page: pageIndex,
			...(artist ? { artist } : {}),
		});
	};

	const handleLoadMore = () => {
		hasClickedLoadMore = true;
		trackLoadMore('button');
		onLoadMore();
	};

	const handleScrollLoadMore = () => {
		trackLoadMore('scroll');
		onLoadMore();
	};
</script>

{#if initialLoadMoreButton && !hasClickedLoadMore}
	{#if hasMore}
		<div class={twMerge('flex justify-center', classes?.button)}>
			<Button
				dataCy={`${dataCy}-load-more`}
				variant="secondary"
				size="md"
				onclick={handleLoadMore}
			>
				Load more
			</Button>
		</div>
	{/if}
{:else}
	<InfiniteScroll
		{hasMore}
		{threshold}
		window
		onloadmore={handleScrollLoadMore}
	/>
{/if}
